import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import {
  Plus, Pencil, Trash2, ArrowUp, ArrowDown, X, Tag,
} from "lucide-react";

export default function CategoriesView({ PageHeader }) {
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, b] = await Promise.all([
        adminApi.get("/admin/category-priorities"),
        adminApi.get("/admin/brands"),
      ]);
      setCats(c.data);
      setBrands(b.data);
    } catch {
      toast.error("Kategoriler alınamadı.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`"${c.label}" kategorisini silmek istediğinize emin misiniz?`)) return;
    try {
      await adminApi.delete(`/admin/category-priorities/${c.id}`);
      toast.success("Kategori silindi.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silme hatası.");
    }
  };

  const movePriority = async (c, brand, direction) => {
    const arr = [...c.brand_priority];
    const idx = arr.indexOf(brand);
    if (idx < 0) return;
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    try {
      await adminApi.put(`/admin/category-priorities/${c.id}`, { brand_priority: arr });
      load();
    } catch (e) {
      toast.error("Sıralama güncellenemedi.");
    }
  };

  const removePriority = async (c, brand) => {
    const arr = c.brand_priority.filter((b) => b !== brand);
    try {
      await adminApi.put(`/admin/category-priorities/${c.id}`, { brand_priority: arr });
      load();
    } catch {
      toast.error("Kaldırılamadı.");
    }
  };

  const addPriority = async (c, brand) => {
    if (c.brand_priority.includes(brand)) return;
    const arr = [...c.brand_priority, brand];
    try {
      await adminApi.put(`/admin/category-priorities/${c.id}`, { brand_priority: arr });
      load();
    } catch {
      toast.error("Eklenemedi.");
    }
  };

  return (
    <div>
      <PageHeader
        title="Kategori Öncelikleri"
        subtitle="Müşteri marka yazmadığında hangi kategoride hangi markaların öncelikli olduğunu yönetin"
        actions={
          <Button
            data-testid={ADMIN.addCategoryBtn}
            onClick={() => setEditing({ key: "", label: "", keywords: [], brand_priority: [], sort_order: (cats.length || 0) + 1 })}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white uppercase tracking-wide text-xs"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Yeni Kategori
          </Button>
        }
      />
      <div className="p-8">
        {loading ? (
          <div className="text-sm text-[#4B5563]">Yükleniyor…</div>
        ) : cats.length === 0 ? (
          <Card className="border-[#E2E8F0] p-12 text-center">
            <Tag className="w-10 h-10 mx-auto text-[#1E3A5F] mb-3" />
            <p className="text-sm text-[#4B5563]">Henüz kategori eklenmemiş.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {cats.map((c) => (
              <Card key={c.id} className="border-[#E2E8F0] rounded-lg overflow-hidden">
                <div className="bg-[#F97316] h-1" />
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-[#1E3A5F] tracking-tight">{c.label}</h3>
                      <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-mono mt-0.5">
                        {c.key}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditing(c)}
                        className="p-1.5 hover:bg-[#F1F5F9] rounded transition-colors"
                        title="Düzenle"
                        data-testid={`admin-category-edit-${c.key}`}
                      >
                        <Pencil className="w-3.5 h-3.5 text-[#4B5563]" />
                      </button>
                      <button
                        onClick={() => remove(c)}
                        className="p-1.5 hover:bg-red-50 rounded transition-colors"
                        title="Sil"
                        data-testid={`admin-category-delete-${c.key}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-[#EF4444]" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-[10px] uppercase tracking-wide text-[#4B5563] mb-1.5">
                      Anahtar Kelimeler
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {c.keywords.map((kw) => (
                        <Badge key={kw} variant="outline" className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1F2937] text-xs font-mono">
                          {kw}
                        </Badge>
                      ))}
                      {c.keywords.length === 0 && (
                        <span className="text-xs text-[#9CA3AF]">— yok —</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#4B5563] mb-2">
                      Marka Öncelik Sırası
                    </div>
                    <div className="space-y-1.5">
                      {c.brand_priority.map((bn, i) => (
                        <div
                          key={bn}
                          data-testid={`admin-category-${c.key}-priority-${i}`}
                          className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md px-2.5 py-1.5"
                        >
                          <span className="text-[10px] font-mono font-bold text-[#F97316] w-5">
                            #{i + 1}
                          </span>
                          <span className="flex-1 text-sm font-medium text-[#1F2937]">{bn}</span>
                          <button
                            disabled={i === 0}
                            onClick={() => movePriority(c, bn, "up")}
                            className="p-1 hover:bg-white rounded disabled:opacity-30"
                            title="Yukarı"
                          >
                            <ArrowUp className="w-3 h-3 text-[#4B5563]" />
                          </button>
                          <button
                            disabled={i === c.brand_priority.length - 1}
                            onClick={() => movePriority(c, bn, "down")}
                            className="p-1 hover:bg-white rounded disabled:opacity-30"
                            title="Aşağı"
                          >
                            <ArrowDown className="w-3 h-3 text-[#4B5563]" />
                          </button>
                          <button
                            onClick={() => removePriority(c, bn)}
                            className="p-1 hover:bg-red-50 rounded"
                            title="Kaldır"
                          >
                            <X className="w-3 h-3 text-[#EF4444]" />
                          </button>
                        </div>
                      ))}
                      {c.brand_priority.length === 0 && (
                        <p className="text-xs text-[#9CA3AF] italic">
                          Henüz marka eklenmedi.
                        </p>
                      )}
                      {/* Add brand selector */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {brands
                          .filter((b) => !c.brand_priority.includes(b.name))
                          .map((b) => (
                            <button
                              key={b.id}
                              onClick={() => addPriority(c, b.name)}
                              className="text-[11px] px-2 py-1 border border-dashed border-[#E2E8F0] rounded hover:border-[#F97316] hover:bg-[#FFF7ED] hover:text-[#F97316] text-[#4B5563] transition-colors"
                            >
                              + {b.name}
                            </button>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <CategoryEditDialog
        key={`cat-${editing?.id || "new"}-${editing ? "open" : "closed"}`}
        cat={editing}
        brands={brands}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function CategoryEditDialog({ cat, brands, onClose, onSaved }) {
  const [form, setForm] = useState({
    key: cat?.key || "",
    label: cat?.label || "",
    keywords: (cat?.keywords || []).join(", "),
    brand_priority: cat?.brand_priority || [],
    sort_order: cat?.sort_order || 0,
  });
  const [saving, setSaving] = useState(false);

  if (!cat) return null;
  const isNew = !cat.id;

  const save = async () => {
    if (!form.key.trim() || !form.label.trim()) {
      return toast.error("Anahtar ve etiket zorunlu.");
    }
    const payload = {
      key: form.key.trim(),
      label: form.label.trim(),
      keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
      brand_priority: form.brand_priority,
      sort_order: form.sort_order,
    };
    setSaving(true);
    try {
      if (isNew) {
        await adminApi.post("/admin/category-priorities", payload);
        toast.success("Kategori eklendi.");
      } else {
        await adminApi.put(`/admin/category-priorities/${cat.id}`, payload);
        toast.success("Kategori güncellendi.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi.");
    } finally { setSaving(false); }
  };

  const toggleBrand = (bn) => {
    setForm((f) => ({
      ...f,
      brand_priority: f.brand_priority.includes(bn)
        ? f.brand_priority.filter((x) => x !== bn)
        : [...f.brand_priority, bn],
    }));
  };

  return (
    <Dialog open={!!cat} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-[#1E3A5F]">
            {isNew ? "Yeni Kategori" : `Kategori: ${cat.label}`}
          </DialogTitle>
          <DialogDescription className="text-[#4B5563] text-xs">
            Anahtar kelimeler virgülle ayrılır (örn: "kombi, premix").
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">
                Anahtar (slug)
              </Label>
              <Input
                data-testid={ADMIN.categoryKeyInput}
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                placeholder="kombi"
                disabled={!isNew}
                className="border-[#E2E8F0] font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">Sıra</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                className="border-[#E2E8F0] tabular-nums"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">Etiket</Label>
            <Input
              data-testid={ADMIN.categoryLabelInput}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Kombi & Yoğuşmalı"
              className="border-[#E2E8F0]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">
              Anahtar Kelimeler (virgülle)
            </Label>
            <Input
              data-testid={ADMIN.categoryKeywordsInput}
              value={form.keywords}
              onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              placeholder="kombi, premix, konvansiyonel"
              className="border-[#E2E8F0]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wide text-[#4B5563]">
              Markalar (sıraya gelecek)
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {brands.map((b) => {
                const sel = form.brand_priority.includes(b.name);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => toggleBrand(b.name)}
                    className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                      sel
                        ? "bg-[#1E3A5F] border-[#1E3A5F] text-white"
                        : "bg-white border-[#E2E8F0] text-[#1F2937] hover:border-[#F97316]"
                    }`}
                  >
                    {b.name}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#9CA3AF]">
              Sıralama oluşturulduktan sonra kartlardan ↑ ↓ ile ayarlanabilir.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button
            data-testid={ADMIN.categorySaveBtn}
            onClick={save}
            disabled={saving}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white"
          >
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
